import { Card, CardContent } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Progress } from "@/shared/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/shared/ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";
import { AssetCard } from "./AssetCard";
import type { CategoryWithAssets, Asset } from "@/features/assets/hooks/usePropertyAssetRegistry";
import { getCategoryStyle } from "@/features/assets/services/assetCategories";

interface HierarchyViewProps {
  categories: CategoryWithAssets[];
  expandedCategories: Set<string>;
  onToggleCategory: (categoryId: string) => void;
  onAssetClick: (asset: Asset) => void;
  selectedAssetId?: string;
}

export function HierarchyView({
  categories,
  expandedCategories,
  onToggleCategory,
  onAssetClick,
  selectedAssetId,
}: HierarchyViewProps) {
  return (
    <div className="space-y-2">
      {categories.map((category) => {
        const isExpanded = expandedCategories.has(category.id);
        // Get the icon from system category styling
        const categoryStyle = getCategoryStyle(category.name);
        const Icon = categoryStyle.icon;
        const assetCount = category.assets.length;
        const completion = assetCount > 0 ? 100 : 0;

        return (
          <div key={category.id}>
            <Collapsible open={isExpanded} onOpenChange={() => onToggleCategory(category.id)}>
              <CollapsibleTrigger asChild>
                <Card className="cursor-pointer hover:bg-accent/50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                        <Icon className="h-5 w-5" style={{ color: categoryStyle.color }} />
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{category.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {assetCount} asset{assetCount !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            {completion}%
                          </span>
                          <Progress value={completion} className="w-16 h-2" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CollapsibleTrigger>

              <CollapsibleContent className="space-y-2 ml-6 mt-2">
                {category.assets.length > 0 ? (
                  category.assets.map((asset, assetIndex) => (
                    <AssetCard
                      key={asset.id}
                      asset={asset}
                      index={assetIndex}
                      onClick={() => onAssetClick(asset)}
                      isSelected={selectedAssetId === asset.id}
                    />
                  ))
                ) : (
                  <Card className="bg-muted/30">
                    <CardContent className="p-4 text-center text-muted-foreground text-sm">
                      No assets in this category
                    </CardContent>
                  </Card>
                )}
              </CollapsibleContent>
            </Collapsible>
          </div>
        );
      })}
    </div>
  );
}
